import { Injectable } from '@angular/core';
import { AclAssetPermission } from '@y42/acl/models';
import {
  DatasourceCTypeModel,
  DatasourceQuery,
  DatasourceTypeQuery,
  DatasourceTypesContentfulService,
  UNKNOWN_DATASOURCE_TYPE_MODEL,
} from '@y42/datasource';
import { Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { FeatureAclQuery } from '@y42/feature/acl';
import { Y42FeatureType } from '@y42/json-schema';
import { assertNonNullable } from '@y42/util/assert';
import { isNotNullish } from '@y42/util/nullish';
import { Observable } from 'rxjs';
import { UiModelingTableJobQuery } from '../datasource-table-job/ui-modeling-table-job.query';
import { UiModelingTableQuery } from '../datasource-table/ui-modeling-table.query';
import { DataModelGoJSModel } from '../models';
import { UiModelingGitService } from './ui-modeling-git.service';
import { UiModelingState, UiModelingUIModel } from './ui-modeling.model';
import { UiModelingStore } from './ui-modeling.store';

@Injectable({ providedIn: 'root' })
export class UiModelingQuery extends DatasourceQuery<UiModelingState, UiModelingUIModel> {
  constructor(
    protected readonly store: UiModelingStore,
    protected readonly datasourceTypeQuery: DatasourceTypeQuery,
    protected readonly datasourceTableQuery: UiModelingTableQuery,
    protected readonly datasourceTableJobQuery: UiModelingTableJobQuery,
    private readonly featureAclQuery: FeatureAclQuery,
    private readonly modelingGitService: UiModelingGitService,
    private readonly datasourceTypesContentfulService: DatasourceTypesContentfulService,
  ) {
    super(Y42FeatureType.UiModel, store, datasourceTypeQuery, datasourceTableQuery, {
      toUIModel: (storeModel) => {
        const { last_used, starred } = this.getValue();
        // Don't remove optional chaining operator, in some very rare cases settings can be undefined
        const $schema = (storeModel.settings as UiModelingUIModel | undefined)?.$schema ?? '';
        const typeModel = this.datasourceTypeQuery.findModelBySchema($schema) ?? UNKNOWN_DATASOURCE_TYPE_MODEL;

        const resourceType = Y42FeatureToAclAssetResourceType[this.featureType];
        assertNonNullable(resourceType);

        const uiModelHasReadPermission = !!this.featureAclQuery.getPermission({
          resourceType,
          assetPath: storeModel.$path,
          permission: AclAssetPermission.Read,
        })?.allowed;

        const tables = storeModel.$children
          .map((id) => this.datasourceTableQuery.getUIEntity(id))
          .filter(isNotNullish)
          .map((table) => ({
            ...table,
            hasReadPermission: uiModelHasReadPermission,
          }));

        return {
          ...storeModel,
          featureType: this.featureType,
          typeIcon: typeModel.logo,
          typeLabel: typeModel.displayName,
          children: tables,
          tables,
          typeModel,
          last_used: last_used[storeModel.$path] || null,
          starred: !!starred[storeModel.$path],
          hasReadPermission: uiModelHasReadPermission,
          ...this.enrichWithUiContent(storeModel.$name),
        };
      },
    });
  }

  readonly types$: Observable<DatasourceCTypeModel[] | null> = this.datasourceTypesContentfulService.data$;

  private enrichWithUiContent(modelName: UiModelingUIModel['$name']) {
    const uiModelPath = this.modelingGitService.getPath(modelName, 'ui-model.json');

    return {
      getUiModel: () =>
        this.modelingGitService
          .readJSON<DataModelGoJSModel & { $schema?: string }>(uiModelPath)
          .then((json) => {
            if (json) {
              delete json['$schema'];
            }
            return json;
          })
          .catch(() => {
            return null;
          }),
    };
  }
}
